import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  CircularProgress,
} from '@mui/material';
import { useCreateFAQ, useUpdateFAQ } from '../hooks/useFAQApi';
import type { FAQ, FAQFormData } from '../types/faq.types';

interface FAQEditorProps {
  open: boolean;
  faq?: FAQ | null;
  onClose: () => void;
  mode: 'create' | 'edit';
}

export const FAQEditor: React.FC<FAQEditorProps> = ({
  open,
  faq,
  onClose,
  mode,
}) => {
  const [formData, setFormData] = useState<FAQFormData>({
    question: '',
    answer: '',
  });

  const createFAQMutation = useCreateFAQ();
  const updateFAQMutation = useUpdateFAQ();

  const isLoading = createFAQMutation.isPending || updateFAQMutation.isPending;

  // Initialize form data when FAQ changes
  useEffect(() => {
    if (mode === 'edit' && faq) {
      setFormData({
        question: faq.question,
        answer: faq.answer,
      });
    } else {
      setFormData({
        question: '',
        answer: '',
      });
    }
  }, [faq, mode]);

  const handleInputChange = (field: keyof FAQFormData, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.question.trim() || !formData.answer.trim()) {
      return;
    }

    try {
      if (mode === 'create') {
        await createFAQMutation.mutateAsync(formData);
      } else if (mode === 'edit' && faq) {
        await updateFAQMutation.mutateAsync({
          id: faq.id,
          data: formData,
        });
      }
      onClose();
    } catch (error) {
      // Error handled by the mutations
    }
  };

  const handleClose = () => {
    if (!isLoading) {
      setFormData({ question: '', answer: '' });
      onClose();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      disableEscapeKeyDown={isLoading}
    >
      <form onSubmit={handleSubmit}>
        <DialogTitle>
          {mode === 'create' ? 'Nouvelle FAQ' : 'Modifier la FAQ'}
        </DialogTitle>

        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, pt: 1 }}>
            <TextField
              label="Question"
              multiline
              rows={3}
              fullWidth
              required
              value={formData.question}
              onChange={(e) => handleInputChange('question', e.target.value)}
              disabled={isLoading}
              placeholder="Saisissez la question fréquemment posée..."
            />

            <TextField
              label="Réponse"
              multiline
              rows={5}
              fullWidth
              required
              value={formData.answer}
              onChange={(e) => handleInputChange('answer', e.target.value)}
              disabled={isLoading}
              placeholder="Saisissez la réponse que l'agent doit donner..."
            />
          </Box>
        </DialogContent>

        <DialogActions>
          <Button
            onClick={handleClose}
            disabled={isLoading}
            color="secondary"
          >
            Annuler
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={isLoading || !formData.question.trim() || !formData.answer.trim()}
            startIcon={isLoading ? <CircularProgress size={16} /> : null}
          >
            {mode === 'create' ? 'Créer' : 'Modifier'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};
